import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Popover, PopoverModule } from 'primeng/popover';
import { HideOnWindowScrollDirective } from '../../directives/hide-on-window-scroll/hide-on-window-scroll.directive';
import { MBadgeComponent } from '../m-badge';
import { MInputSearchComponent } from '../m-input-search';
import { MNavTriggerComponent } from '../m-nav-trigger';
import type {
  MegaMenuItem,
  MegaMenuItemClickEvent,
  MegaMenuTrigger
} from './m-mega-menu.types';

@Component({
  selector: 'm-mega-menu',
  templateUrl: './m-mega-menu.component.html',
  styleUrls: ['./m-mega-menu.component.scss'],
  imports: [
    NgTemplateOutlet,
    FormsModule,
    RouterLink,
    PopoverModule,
    HideOnWindowScrollDirective,
    MNavTriggerComponent,
    MBadgeComponent,
    MInputSearchComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MMegaMenuComponent {
  readonly popover = viewChild.required<Popover>('popover');
  private readonly search = viewChild(MInputSearchComponent);

  readonly trigger = input<MegaMenuTrigger>({});
  readonly items = input<MegaMenuItem[]>([]);
  readonly columns = input<1 | 2 | 3>(1);
  readonly panelWidth = input<string>();
  readonly disabled = input(false);
  readonly hideArrow = input(false);
  readonly active = input(false);
  readonly showDot = input(false);
  /** Shows a search field above the grid that filters items by label/description. */
  readonly searchable = input(false);
  readonly searchPlaceholder = input('Search');

  readonly itemClicked = output<MegaMenuItemClickEvent>();
  readonly panelShow = output<void>();
  readonly panelHide = output<void>();

  readonly isOpen = signal(false);
  readonly query = signal('');

  readonly visibleItems = computed(() => {
    const q = this.query().trim().toLowerCase();
    if (!this.searchable() || !q) {
      return this.items();
    }
    return this.items().filter((item) =>
      `${item.label} ${item.description ?? ''}`.toLowerCase().includes(q)
    );
  });

  togglePanel(event: Event): void {
    if (this.disabled()) {
      return;
    }
    this.popover().toggle(event);
  }

  onShow(): void {
    this.isOpen.set(true);
    this.panelShow.emit();
    // Panel content only exists once the popover is shown.
    setTimeout(() => this.search()?.focus());
  }

  onHide(): void {
    this.isOpen.set(false);
    this.query.set('');
    this.panelHide.emit();
  }

  onItemClick(item: MegaMenuItem, event: Event): void {
    if (item.disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    item.command?.(event);
    this.itemClicked.emit({ item, event });
    this.popover().hide();
  }
}
